import { chunk, clamp, inRange } from 'lodash-es';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filter, fromEvent, map, of, switchMap, takeUntil, tap } from 'rxjs';

import { useSubscriptions } from '@/package';
import { createIntersectionObserver, withTarget } from '@/util';

import TickerItem from './TickerItem';
import style from './style';
import useToolPlayControllerContext from './useToolPlayControllerContext';

type ToolPlayControllerFrameBarTickerListProps = {
  activeRange?: [number, number];
  onChange: React.Dispatch<number>;
};

const CHUNKING_SIZE = 300;

const ToolPlayControllerFrameBarTickerList = memo(
  ({ activeRange, onChange }: ToolPlayControllerFrameBarTickerListProps) => {
    const {
      index,
      isFrameFileLoading,
      preloadedFrameSeqList,
      tick,
      tickInterval,
      tickWidth,
      tickerList,
      visibleOnlyMyFrame,
      zoom,

      isAssignedMeFrameSeq,
    } = useToolPlayControllerContext();
    const [tickerPageIndex, setTickerPageIndex] = useState(-1);
    const lastPreloadedFrameSeq =
      preloadedFrameSeqList[preloadedFrameSeqList.length - 1];
    const isScrolling = useRef(false);
    const chunkedTickerList = useMemo(
      () => chunk(tickerList, CHUNKING_SIZE),
      [tickerList.length],
    );
    const visibleChunkedTickerListRange = useMemo(() => {
      const min = clamp(tickerPageIndex - 1, 0, chunkedTickerList.length - 3);
      const max = clamp(min + 2, min + 1, chunkedTickerList.length - 1);

      return [min, max] as [number, number];
    }, [chunkedTickerList.length, tickerPageIndex]);
    const visibleChunkedTickerList = useMemo(() => {
      const [min, max] = visibleChunkedTickerListRange;

      return chunkedTickerList.slice(min, max + 1);
    }, [chunkedTickerList, visibleChunkedTickerListRange]);
    const flatVisibleTickerList = useMemo(
      () => visibleChunkedTickerList.flat(),
      [visibleChunkedTickerList],
    );
    const getAbsoluteIndex = useCallback(
      (index: number) =>
        visibleChunkedTickerListRange[0] * CHUNKING_SIZE + index,
      [tickerPageIndex, zoom],
    );
    const getRelativeIndex = useCallback(
      (index: number) =>
        index - visibleChunkedTickerListRange[0] * CHUNKING_SIZE,
      [tickerPageIndex, zoom],
    );
    const updateGrabPosition = useCallback(
      (index: number) => {
        if (!grabEl.current) return;

        const newGrabLeft = getRelativeIndex(index / tickInterval) * tickWidth;

        if (newGrabLeft > tickerListEl.current!.scrollWidth) {
          grabEl.current.style.display = 'none';
          return;
        }

        grabEl.current.style.display = '';
        grabEl.current.style.setProperty('--left', `${newGrabLeft}px`);
      },
      [chunkedTickerList, index, tickerPageIndex, zoom],
    );

    const isBoundary = useCallback(
      (ticker: number): boolean => activeRange?.includes(ticker) ?? false,
      [activeRange],
    );
    const isInActiveRange = useCallback(
      (ticker: number): boolean =>
        activeRange
          ? inRange(ticker, activeRange[0], activeRange[1] + 1)
          : true,
      [activeRange],
    );
    const tickerOffset = Math.min(...(activeRange || [1]));

    const tickerListEl = useRef<HTMLDivElement>(null);
    const grabEl = useRef<HTMLDivElement>(null);
    const leftThresholderEl = useRef<HTMLDivElement>(null);
    const rightThresholderEl = useRef<HTMLDivElement>(null);

    useSubscriptions(
      () => [
        of(rightThresholderEl.current)
          .pipe(
            filter(el => !!el),
            filter(() => chunkedTickerList.length > 0),
            filter(() => tickerPageIndex < chunkedTickerList.length - 1),
            switchMap(el =>
              createIntersectionObserver(el, {
                root: null,
                rootMargin: '0px',
                threshold: 1,
              }),
            ),
            tap(value => {
              value && setTickerPageIndex(index => index + 1);
            }),
          )
          .subscribe(),

        of(leftThresholderEl.current)
          .pipe(
            filter(el => !!el),
            filter(() => chunkedTickerList.length > 0),
            filter(() => tickerPageIndex > 0),
            switchMap(el =>
              createIntersectionObserver(el, {
                root: null,
                rootMargin: '0px',
                threshold: 1,
              }),
            ),
            tap(value => {
              value && setTickerPageIndex(index => index - 1);
            }),
          )
          .subscribe(),
      ],
      [chunkedTickerList, tickerPageIndex],
    );

    useSubscriptions(
      () => [
        fromEvent<MouseEvent>(window, 'mousedown')
          .pipe(
            withTarget,
            filter(
              e =>
                e.target === grabEl.current &&
                tickerListEl.current != null &&
                tickerPageIndex >= 0,
            ),
            switchMap(e => {
              const target = grabEl.current!;
              const prevLeft = +target.style
                .getPropertyValue('--left')
                ?.replace('px', '');
              const sx = e.clientX;
              const [minLeft, maxLeft] = [0, tickerListEl.current!.scrollWidth];
              let ex = sx;

              return fromEvent<MouseEvent>(window, 'mousemove').pipe(
                map(e => e.clientX),
                map(mx => mx - sx),
                filter(diffX => Math.abs(diffX) > 0),
                tap(diffX => {
                  target.style.setProperty(
                    '--left',
                    `${clamp(diffX + prevLeft, minLeft, maxLeft)}px`,
                  );

                  ex = diffX + prevLeft;
                }),
                takeUntil(
                  fromEvent(window, 'mouseup').pipe(
                    filter(() => ex != sx),
                    tap(() => {
                      const newIndex = getAbsoluteIndex(
                        Math.round(clamp(ex, minLeft, maxLeft) / tickWidth),
                      );

                      updateGrabPosition(newIndex);

                      onChange(newIndex * tickInterval);
                    }),
                  ),
                ),
              );
            }),
          )
          .subscribe(),

        fromEvent<MouseEvent>(window, 'click')
          .pipe(
            withTarget,
            filter(e => !!e.target.dataset.ticker),
            map(e => +e.target.dataset.ticker!),
            map(ticker => tickerList.findIndex(v => v === ticker)),
            filter(
              index =>
                index >= 0 &&
                (visibleOnlyMyFrame ? isAssignedMeFrameSeq(index + 1) : true),
            ),
            tap(index => onChange(index * tickInterval)),
          )
          .subscribe(),
      ],
      [
        index,
        tickerList,
        tickInterval,
        tickerPageIndex,
        visibleOnlyMyFrame,
        zoom,
      ],
    );

    useSubscriptions(
      () => [
        fromEvent<Event>(tickerListEl.current!, 'scroll')
          .pipe(
            tap(() => {
              !isScrolling.current && (isScrolling.current = true);
            }),
            switchMap(() =>
              fromEvent<Event>(tickerListEl.current!, 'scrollend').pipe(
                tap(() => {
                  isScrolling.current = false;
                }),
              ),
            ),
          )
          .subscribe(),
      ],
      [],
    );

    useEffect(() => {
      if (index < 0 || tickerPageIndex < 0 || !grabEl.current) return;

      updateGrabPosition(index);
    }, [index, tickerPageIndex, zoom]);

    useEffect(() => {
      if (index < 0 || !grabEl.current) return;

      setTickerPageIndex(Math.floor(index / tickInterval / CHUNKING_SIZE));
    }, [index, zoom]);

    useEffect(() => {
      const $tickerList = tickerListEl.current;
      const $ticker = $tickerList?.querySelector(
        `[data-ticker="${tickerList[index / tickInterval]}"]`,
      );

      if (
        !$tickerList ||
        !$ticker ||
        tickerPageIndex < 0 ||
        isScrolling.current
      )
        return;

      const { offsetWidth } = $tickerList;

      $tickerList.scrollTo({
        left:
          getRelativeIndex(index / tickInterval) * tickWidth -
          (offsetWidth >> 1),
      });
    }, [index, tickerPageIndex, tickInterval, tickerList, zoom]);

    useEffect(() => {
      const thresholderEls = [
        rightThresholderEl.current,
        leftThresholderEl.current,
      ];

      thresholderEls.forEach(el => {
        if (!el) return;

        el.style.display = 'none';

        setTimeout(() => (el.style.display = ''), 500);
      });
    }, [tickerPageIndex]);

    return (
      <div ref={tickerListEl} className={style.tickerList}>
        <div ref={grabEl} role="button" className={style.tickerListGrap}></div>
        <div
          className={style.tickerListContainer}
          style={{
            width: `${visibleChunkedTickerList.flat(2).length * tickWidth}px`,
          }}
          role="list"
        >
          <div
            ref={leftThresholderEl}
            className={style.tickerListContainerThresholder}
          />

          {chunkedTickerList.length > 0 &&
            flatVisibleTickerList.map(ticker => (
              <TickerItem
                key={ticker}
                ticker={ticker}
                tickWidth={tickWidth}
                index={index}
                isFrameFileLoading={isFrameFileLoading}
                preloadedFrameSeqList={preloadedFrameSeqList}
                lastPreloadedFrameSeq={lastPreloadedFrameSeq}
                isAssignedMeFrameSeq={isAssignedMeFrameSeq}
                isBoundary={isBoundary(ticker)}
                isInActiveRange={isInActiveRange(ticker)}
                tickerOffset={tickerOffset}
                tick={tick}
                tickInterval={tickInterval}
                visibleOnlyMyFrame={visibleOnlyMyFrame}
              />
            ))}

          <div
            ref={rightThresholderEl}
            className={style.tickerListContainerThresholder}
          />
        </div>
      </div>
    );
  },
);

export default ToolPlayControllerFrameBarTickerList;
